import { FunctionNode } from '../../../types/definition';
import { SchemaBuilder } from '../../schema-builder/SchemaBuilder';
import { InputMappingEditor } from './InputMappingEditor';

interface FunctionNodePropertiesProps {
  node: FunctionNode;
  onChange: (updates: Partial<FunctionNode>) => void;
  /**
   * Inspect, do not edit (task 881). Every control renders the same, disabled,
   * with no change handler attached — including the function select, which
   * still shows the function's name rather than a blank.
   */
  readOnly?: boolean;
}

const AVAILABLE_FUNCTIONS = [
  { name: 'echo', description: 'Returns input as output unchanged' },
  { name: 'merge', description: 'Merges multiple inputs into one object' },
  { name: 'delay', description: 'Delays execution for specified time' },
  { name: 'transform', description: 'Transforms data using a template' },
];

export function FunctionNodeProperties({ node, onChange, readOnly = false }: FunctionNodePropertiesProps) {
  return (
    <div className="space-y-4" aria-readonly={readOnly || undefined}>
      {/* Node Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Name
        </label>
        <input
          type="text"
          value={node.name || ''}
          onChange={readOnly ? undefined : (e) => onChange({ name: e.target.value })}
          disabled={readOnly}
          placeholder="Function"
          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                     focus:border-purple-500 focus:ring-purple-500
                     dark:bg-gray-700 dark:text-white text-sm"
        />
      </div>

      {/* Function Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Function <span className="text-red-500">*</span>
        </label>
        <select
          value={node.function || ''}
          onChange={readOnly ? undefined : (e) => onChange({ function: e.target.value })}
          disabled={readOnly}
          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                     focus:border-purple-500 focus:ring-purple-500
                     dark:bg-gray-700 dark:text-white text-sm"
        >
          <option value="">Select a function...</option>
          {AVAILABLE_FUNCTIONS.map((fn) => (
            <option key={fn.name} value={fn.name}>
              {fn.name}
            </option>
          ))}
        </select>
        {node.function && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {AVAILABLE_FUNCTIONS.find(f => f.name === node.function)?.description}
          </p>
        )}
      </div>

      {/* Function-specific config */}
      {node.function === 'delay' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Delay (ms)
          </label>
          <input
            type="number"
            min="0"
            value={(node.config?.delayMs as number) || ''}
            onChange={
              readOnly
                ? undefined
                : (e) => {
                    const delayMs = e.target.value ? parseInt(e.target.value) : undefined;
                    onChange({ config: { ...node.config, delayMs } });
                  }
            }
            disabled={readOnly}
            placeholder="1000"
            className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                       focus:border-purple-500 focus:ring-purple-500
                       dark:bg-gray-700 dark:text-white text-sm"
          />
        </div>
      )}

      {node.function === 'transform' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Transform Template
          </label>
          <textarea
            value={(node.config?.template as string) || ''}
            onChange={
              readOnly
                ? undefined
                : (e) => {
                    onChange({ config: { ...node.config, template: e.target.value } });
                  }
            }
            disabled={readOnly}
            placeholder='{"result": "{{input.value}}"}'
            rows={4}
            className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                       focus:border-purple-500 focus:ring-purple-500
                       dark:bg-gray-700 dark:text-white text-sm font-mono"
          />
        </div>
      )}

      {/* Entry Point Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Entry Point
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Re-executes on collaboration continuation
          </p>
        </div>
        <button
          type="button"
          onClick={readOnly ? undefined : () => onChange({ entry: !node.entry })}
          disabled={readOnly}
          className={`
            relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
            transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
            ${node.entry ? 'bg-purple-600' : 'bg-gray-200 dark:bg-gray-600'}
          `}
        >
          <span
            className={`
              pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
              transition duration-200 ease-in-out
              ${node.entry ? 'translate-x-5' : 'translate-x-0'}
            `}
          />
        </button>
      </div>

      {/* Input Schema */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <SchemaBuilder
          label="Input Schema"
          value={node.input ?? null}
          readOnly={readOnly}
          onChange={(schema) => onChange({ input: schema ?? undefined })}
        />
      </div>

      {/* Input Mapping */}
      <div>
        <InputMappingEditor
          value={node.inputMapping}
          readOnly={readOnly}
          onChange={(mapping) => onChange({ inputMapping: mapping })}
          schema={node.input}
        />
      </div>

      {/* Output Schema */}
      <div>
        <SchemaBuilder
          label="Output Schema"
          value={node.output ?? null}
          readOnly={readOnly}
          onChange={(schema) => onChange({ output: schema ?? undefined })}
        />
      </div>
    </div>
  );
}
